import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Pause, Play, Terminal } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { formatMoneyString, formatTimestamp, PROTOCOL_COLORS } from '@/lib/utils';
import type { AgentQuery, Protocol, QueryOutcome } from '@/types/app';

const OUTCOME_BADGE_VARIANT: Record<QueryOutcome, 'success' | 'acp' | 'danger'> = {
  PURCHASED: 'success',
  MATCHED:   'acp',
  REJECTED:  'danger',
};

export const AgentLogPage = ({
  agentQueries, isLiveFeedActive, onToggleFeed,
}: {
  agentQueries:     AgentQuery[];
  isLiveFeedActive: boolean;
  onToggleFeed:     () => void;
}) => {
  const [search,        setSearch]        = useState('');
  const [filterProto,   setFilterProto]   = useState<Protocol | 'ALL'>('ALL');
  const [filterOutcome, setFilterOutcome] = useState<QueryOutcome | 'ALL'>('ALL');

  const filtered = useMemo(() =>
    agentQueries.filter((q) => {
      if (filterProto   !== 'ALL' && q.protocol !== filterProto)   return false;
      if (filterOutcome !== 'ALL' && q.outcome  !== filterOutcome) return false;
      if (search &&
          !q.queryIntent.toLowerCase().includes(search.toLowerCase()) &&
          !q.agentId.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }),
  [agentQueries, filterProto, filterOutcome, search]);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-zinc-400 text-sm">
          Complete ledger of agent interactions — {agentQueries.length.toLocaleString()} events recorded.
        </p>
        <Button variant="outline" size="sm" onClick={onToggleFeed}>
          {isLiveFeedActive
            ? <><Pause className="w-3.5 h-3.5 mr-1.5" />Pause</>
            : <><Play  className="w-3.5 h-3.5 mr-1.5" />Resume</>}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-zinc-900/50 p-4 rounded-xl border border-zinc-800">
        <Input
          placeholder="Search agent ID or intent…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <select
          value={filterProto}
          onChange={(e) => setFilterProto(e.target.value as Protocol | 'ALL')}
          className="bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-sm
                     text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
        >
          {(['ALL', 'A2A', 'ACP', 'UCP', 'NLIP'] as const).map((p) => (
            <option key={p} value={p}>{p === 'ALL' ? 'All Protocols' : p}</option>
          ))}
        </select>
        <select
          value={filterOutcome}
          onChange={(e) => setFilterOutcome(e.target.value as QueryOutcome | 'ALL')}
          className="bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-sm
                     text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
        >
          {(['ALL', 'PURCHASED', 'MATCHED', 'REJECTED'] as const).map((o) => (
            <option key={o} value={o}>{o === 'ALL' ? 'All Outcomes' : o}</option>
          ))}
        </select>
        <div className="ml-auto">
          <Button variant="outline" size="sm">
            <Download className="w-3.5 h-3.5 mr-1.5" />Export
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h2 className="font-semibold text-sm">Agent Transactions</h2>
          <span className="text-xs text-zinc-500">{filtered.length} events</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                {['Timestamp', 'Agent ID', 'Protocol', 'Intent', 'Product', 'Outcome', 'Amount', 'Latency'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              <AnimatePresence initial={false}>
                {filtered.slice(0, 100).map((q) => (
                  <motion.tr key={q.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="hover:bg-zinc-800/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500 whitespace-nowrap">
                      {formatTimestamp(q.timestamp)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-300 max-w-[140px] truncate">
                      {q.agentId}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold"
                        style={{ color: PROTOCOL_COLORS[q.protocol] }}>
                        {q.protocol}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 max-w-[200px] truncate">
                      {q.queryIntent}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 max-w-[160px] truncate text-xs">
                      {q.productMatched ?? <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={OUTCOME_BADGE_VARIANT[q.outcome]}>
                        {q.outcome}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {q.amount != null
                        ? <span className="text-emerald-400">{formatMoneyString(q.amount)}</span>
                        : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      {q.latencyMs != null ? `${q.latencyMs}ms` : '—'}
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
            <Terminal className="w-8 h-8 mb-3 opacity-40" />
            <p className="text-sm">No events match your filters</p>
          </div>
        )}
        {filtered.length > 100 && (
          <div className="px-5 py-3 border-t border-zinc-800 text-xs text-zinc-500 text-center">
            Showing 100 of {filtered.length.toLocaleString()} events
          </div>
        )}
      </Card>
    </div>
  );
};
